import { useId, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { FileDown } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDate, formatDateTime } from '@blimo/shared';
import { DataTable, ErrorState, Input, Label, PageHeader, StatusBadge } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { PropertyLink } from '../../components/data.jsx';
import { PropertyFilter } from '../finance/panels.jsx';
import { useListState } from '../../lib/list-search.js';

const CONFIRMED_OPTIONS = [
  { value: '1', label: 'Bestätigt' },
  { value: '0', label: 'Code ausstehend' },
];

/** "1"/"0" in the URL → boolean filter (undefined = alle). @param {string|undefined} value */
const toBool = (value) => (value === '1' ? true : value === '0' ? false : undefined);

/** Eigentümerversammlung › Stimmrechtsvollmachten (read-only Übersicht, F-1001). */
export function ProxyAuthorizationsPage() {
  const api = useApi();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const params = {
    page: list.apiParams.page,
    per_page: list.apiParams.per_page,
    filter: { property_id: search.property, from: search.from, to: search.to, confirmed: toBool(search.confirmed) },
  };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.proxyAuthorizations(api, params));
  const columns = useMemo(() => proxyColumns(api), [api]);

  return (
    <>
      <PageHeader
        title="Stimmrechtsvollmachten"
        description="Von Eigentümern im Portal erteilte Vollmachten für Eigentümerversammlungen. Das PDF steht nach der Bestätigung per Code bereit."
      />
      <FilterBar>
        <PropertyFilter value={search.property} onChange={(v) => list.setFilter('property', v)} />
        <DateFilter label="Versammlung ab" value={search.from} onChange={(v) => list.setFilter('from', v)} />
        <DateFilter label="Versammlung bis" value={search.to} onChange={(v) => list.setFilter('to', v)} />
        <FilterSelect label="Status" value={search.confirmed} onChange={(v) => list.setFilter('confirmed', v)} options={CONFIRMED_OPTIONS} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Stimmrechtsvollmachten"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyTitle="Keine Vollmachten"
          emptyDescription="Für die gewählten Filter wurden keine Vollmachten erteilt."
        />
      )}
    </>
  );
}

/** @param {{ label: string, value?: string, onChange: (value: string|undefined) => void }} props */
function DateFilter({ label, value, onChange }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input id={id} type="date" className="h-8 sm:w-40" value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)} />
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- Spalten werden im Liegenschafts-Tab wiederverwendet
export function proxyColumns(api) {
  return [
    {
      id: 'meeting_date',
      header: 'Versammlung',
      enableSorting: false,
      cell: ({ row }) => <span className="numeric">{formatDate(row.original.meeting_date)}</span>,
    },
    { id: 'property', header: 'Liegenschaft', enableSorting: false, cell: ({ row }) => <PropertyLink property={row.original.property} /> },
    {
      id: 'weg',
      header: 'WEG',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span>{row.original.weg_name || '–'}</span>
          {row.original.weg_location && <span className="text-xs text-muted-foreground">{row.original.weg_location}</span>}
        </span>
      ),
    },
    {
      id: 'principal',
      header: 'Vollmachtgeber',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="font-medium">{row.original.principal_name}</span>
          <span className="text-xs text-muted-foreground">{row.original.principal_address}</span>
        </span>
      ),
    },
    {
      id: 'attorney',
      header: 'Bevollmächtigt',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span>{row.original.attorney_type_label ?? row.original.attorney_type}</span>
          {row.original.attorney_name && <span className="text-xs text-muted-foreground">{row.original.attorney_name}</span>}
        </span>
      ),
    },
    {
      id: 'user',
      header: 'Erstellt von',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.user ? (
          <span className="flex flex-col">
            <span>{row.original.user.name}</span>
            <span className="text-xs text-muted-foreground">{row.original.user.email}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">–</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-col gap-0.5">
          <StatusBadge domain="proxyAuthorization" status={row.original.is_confirmed ? 'confirmed' : 'pending'} />
          {row.original.confirmed_at && <span className="numeric text-xs text-muted-foreground">{formatDateTime(row.original.confirmed_at)}</span>}
        </span>
      ),
    },
    {
      id: 'pdf',
      header: 'PDF',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.is_confirmed ? (
          <a
            href={api.admin.proxyAuthorizations.pdfUrl(row.original.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
            aria-label={`Vollmacht von ${row.original.principal_name} als PDF öffnen`}
          >
            <FileDown className="size-3.5" aria-hidden="true" /> PDF
          </a>
        ) : (
          <span className="text-muted-foreground">–</span>
        ),
    },
  ];
}
